// HUMINT recordings — the light questioning (PRD-0032 UC-8, docs/slices/humint-questioning-light).
//
// A recording is a capture of kind `audio`: it goes up through the capture endpoint once it has
// ended, keyed by a client UUID that every retry repeats. The recording view is audited — one
// `session.view` row per call — so a caller fetches it once per view and never polls it; the
// state poll answers ids and states only and records nothing. A page of the case's recordings
// (*Výsluchy*) is audited too: one `subject.view` row naming what it returned. The audio sits
// behind the files route (one `file.read` row per fetch). Every read says where the recording was
// held and who else took part (ADR-0057); `setEncounter` sets both, after one `encounter.link`
// row. Imported by path, not via the barrel.
import type { AudioContainer } from '@/utils/audio-playback'
import type { CaseRole, ProcessingStageDto } from './cases'
import { apiClient } from './client'
import type { CaptureResponse, CaptureUploadOptions } from './humint'
import { humintPath } from './humint-path'
import type { RecordingListItemDto, RecordingStateDto } from './humint-subject-page'
import type { RecordingVoiceFields } from './humint-voice-matching'

// The row every read of a recording shares lives with the subject page's types, which list it too.
export type { RecordingListItemDto, RecordingStateDto } from './humint-subject-page'

/** The containers the server takes (it sniffs the bytes), as the upload's file extension. */
export type RecordingExtension = AudioContainer

/** The largest recording the capture endpoint stores (`capture_too_large` above it). */
export const MAX_RECORDING_BYTES = 95 * 1024 * 1024

/** A recording is large and a field link slow: the client gives its upload this long. */
export const RECORDING_UPLOAD_TIMEOUT_MS = 15 * 60_000

/** One recording upload: the exact bytes and what the server needs to store and transcribe them. */
export interface RecordingUpload {
  /** Client UUID, the idempotency key: every retry sends the same one. */
  captureId: string
  blob: Blob
  /** Hex SHA-256 of `blob`; the server refuses the upload when its own hash differs. */
  sha256: string
  extension: RecordingExtension
  /** When the recording started; for a picked file, when it was picked. ISO. */
  capturedAt: string
  /**
   * The Whisper codes of the languages spoken (`sk`, `uk`, …), in the order chosen. None leaves
   * one to detection, one has everything transcribed in it, several have each speaker transcribed
   * in their own; the server takes four at most.
   */
  languages: readonly string[]
  /** The encounter the recording joins (`enc-<uuid>`); null for none. */
  sameEncounterRef: string | null
  /** The other subjects who took part: subjects of that encounter. Empty without one. */
  participantRefs: readonly string[]
}

/** One stretch of speech by one voice. */
export interface RecordingTurnDto {
  index: number
  start_ms: number
  end_ms: number
  /** 1..n by first appearance; 0 = a voice the transcription could not tell. */
  voice: number
  text: string
  /**
   * The language the turn was transcribed in (a Whisper code, `sk`); null when the transcript
   * names none. A server from before turns named theirs sends no such field.
   */
  language?: string | null
}

export interface RecordingTranscriptDto {
  /** `pending`: none stored yet; `empty`: stored, with no speech in it. */
  status: 'pending' | 'ready' | 'empty'
  voice_count: number
  /** In time order. */
  turns: RecordingTurnDto[]
}

export interface RecordingResponse extends RecordingListItemDto, RecordingVoiceFields {
  /** The `session.view` audit row's correlation id. */
  view_id: string
  /**
   * One language for a reader that shows one: the one with the most speech; before the
   * transcription, the first one named for the recording.
   */
  language: string | null
  /**
   * Every language the recording was transcribed in, in the order first spoken; before the
   * transcript, the ones named for it. Each turn says which of them it is in. A server from
   * before a recording could have several sends no list: `language` is all it names.
   */
  languages?: string[]
  /**
   * Why the recording was transcribed in one language although several were named for it: the
   * transcription's short code (`diarization_off`, `no_turns`, `no_language`, `error`). Null when
   * it was transcribed as asked and before there is a transcript; absent from an older server.
   */
  language_fallback?: string | null
  /** `/api/v1/files/…`, fetched with the operator's token into a blob. */
  audio_url: string
  /** `playback`: a copy every browser plays and seeks; `original`: as received (may not play). */
  audio_source: 'playback' | 'original'
  transcript: RecordingTranscriptDto
  /** The graph element id of the node the case graph shows for the recording's file; null for none. */
  document_node_ref: string | null
  /** The caller's role as this read granted it; a superuser reads as `owner`. */
  member_role: CaseRole
}

/** One page of the case's recordings (*Výsluchy*), newest first. */
export interface RecordingListResponse {
  items: RecordingListItemDto[]
  /** Every recording of the case. */
  total: number
  offset: number
  limit: number
}

/** How many recordings one page of *Výsluchy* asks for. */
const RECORDING_LIST_PAGE_SIZE = 50

/** Where a recording is held and who else took part, set as a whole: who is left out is removed. */
export interface RecordingEncounterRequest {
  /** `enc-<uuid>`; null holds the recording in no encounter, and then it names no participants. */
  encounter_ref: string | null
  /** The other subjects of the recording, without its holder. */
  participant_refs: string[]
}

export interface RecordingEncounterResponse extends RecordingEncounterRequest {
  capture_id: string
  /** The encounter the recording was held in before. */
  previous_encounter_ref: string | null
}

/** What the state poll answers: nothing about the subject or the speech. */
export interface RecordingStateResponse {
  capture_id: string
  state: RecordingStateDto
  transcript_ready: boolean
  playback_ready: boolean
  /** The stage the latest job is in, with every answer; an older server sends none. */
  stage?: ProcessingStageDto | null
  /** The stage the job failed in, else null. */
  failed_stage?: ProcessingStageDto | null
}

/**
 * The languages as the capture form takes them: `languages`, the list as JSON text (a repeated
 * field would be read as one value), left out when none is named. One language also goes in
 * `language`, the field a server from before the list reads, so it still transcribes in it.
 */
function appendLanguages(form: FormData, languages: readonly string[]): void {
  if (languages.length === 0) return
  form.append('languages', JSON.stringify(languages))
  if (languages.length === 1) form.append('language', languages[0])
}

function recordingForm(upload: RecordingUpload): FormData {
  const form = new FormData()
  form.append('file', upload.blob, `${upload.captureId}.${upload.extension}`)
  form.append('capture_id', upload.captureId)
  form.append('sha256', upload.sha256)
  form.append('kind', 'audio')
  form.append('captured_at', upload.capturedAt)
  appendLanguages(form, upload.languages)
  if (upload.sameEncounterRef) form.append('same_encounter_ref', upload.sameEncounterRef)
  // JSON text, like every list the capture form takes: a repeated field would be read as one value.
  if (upload.participantRefs.length > 0) {
    form.append('participant_refs', JSON.stringify(upload.participantRefs))
  }
  return form
}

/** A `/api/…` URL the server hands out, as the path the client asks for (it adds `/api` itself). */
const clientPath = (apiUrl: string): string => apiUrl.replace(/^\/api(?=\/)/, '')

export const humintRecordingsApi = {
  /** Store a recording as the subject's `audio` capture; 201 for a new one, 200 for a replay. */
  upload: (
    caseId: string,
    subjectRef: string,
    upload: RecordingUpload,
    options: CaptureUploadOptions = {}
  ) =>
    apiClient.upload<CaptureResponse>(
      humintPath(caseId, 'subjects', subjectRef, 'captures'),
      recordingForm(upload),
      { ...options, timeoutMs: RECORDING_UPLOAD_TIMEOUT_MS }
    ),

  /** One page of the case's recordings; the server records one `subject.view` row per call. */
  list: (caseId: string, offset: number, signal?: AbortSignal) =>
    apiClient.get<RecordingListResponse>(humintPath(caseId, 'recordings'), {
      params: { offset, limit: RECORDING_LIST_PAGE_SIZE },
      signal,
    }),

  /**
   * Hold the recording in the encounter named (or in none) with exactly the participants named;
   * the server records one `encounter.link` row before it writes. The same request again changes
   * nothing.
   */
  setEncounter: (caseId: string, captureId: string, body: RecordingEncounterRequest) =>
    apiClient.put<RecordingEncounterResponse>(
      humintPath(caseId, 'recordings', captureId, 'encounter'),
      body
    ),

  /** The recording view; the server records one `session.view` row per call (a 503 records none). */
  get: (caseId: string, captureId: string, signal?: AbortSignal) =>
    apiClient.get<RecordingResponse>(humintPath(caseId, 'recordings', captureId), { signal }),

  /**
   * Where the recording's job stands, for polling while it is processing. Ids and states only,
   * so it is not audited; the view that showed the recording was.
   */
  state: (caseId: string, captureId: string, signal?: AbortSignal) =>
    apiClient.get<RecordingStateResponse>(humintPath(caseId, 'recordings', captureId, 'state'), {
      signal,
    }),

  /** Replace the transcript by processing the stored audio in one explicitly chosen language. */
  reprocess: (caseId: string, captureId: string, language: string) =>
    apiClient.post<RecordingStateResponse>(
      humintPath(caseId, 'recordings', captureId, 'reprocess'),
      { language }
    ),

  /** The bytes behind a view's `audio_url`, as the files route answers them (one `file.read` row). */
  audio: (audioUrl: string, signal?: AbortSignal) =>
    apiClient.get<Blob>(clientPath(audioUrl), { responseType: 'blob', signal }),
}
